import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
} from 'firebase/firestore';

// User roles constants
export const USER_ROLES = {
  ADMIN: 'admin',
  SHARED: 'shared',
  PUBLIC: 'public'
};

/**
 * Get user by email from Firestore (fast direct lookup with query fallback)
 */
export const getUserByEmail = async (db, email) => {
  try {
    if (!email) return null;

    // Fast point-lookup by document ID (email)
    const userDocRef = doc(db, 'users', email);
    const userDocSnap = await getDoc(userDocRef);
    if (userDocSnap.exists()) {
      return {
        id: userDocSnap.id,
        ...userDocSnap.data()
      };
    }

    // Fallback: search by email field if doc ID differs
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      return {
        id: userDoc.id,
        ...userDoc.data()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting user by email:', error);
    throw error;
  }
};

/**
 * Create or update user in Firestore
 */
export const createOrUpdateUser = async (db, userData, existingUserData = null) => {
  try {
    const { email, ...otherData } = userData;
    
    // Check if user already exists (use passed data if available to avoid duplicate roundtrips)
    const existingUser = existingUserData || await getUserByEmail(db, email);
    
    if (existingUser) {
      // Update existing user
      const userRef = doc(db, 'users', existingUser.id);
      await updateDoc(userRef, {
        ...otherData,
        updatedAt: new Date(),
        lastLogin: new Date()
      });
      
      return {
        id: existingUser.id,
        email,
        ...existingUser,
        ...otherData,
        updatedAt: new Date(),
        lastLogin: new Date()
      };
    } else {
      // Create new user
      const userRef = doc(db, 'users', email);
      const newUserData = {
        email,
        ...otherData,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastLogin: new Date(),
        isActive: true
      };
      
      await setDoc(userRef, newUserData);
      
      return {
        id: userRef.id,
        ...newUserData
      };
    }
  } catch (error) {
    console.error('Error creating/updating user:', error);
    throw error;
  }
};

/**
 * Get all users from Firestore
 */
export const getAllUsers = async (db, tenant = 'default') => {
  try {
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return users;
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};

/**
 * Update user role
 */
export const updateUserRole = async (db, userId, role) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      role,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating user role:', error);
    throw error;
  }
};

/**
 * Toggle user active status
 */
export const toggleUserActiveStatus = async (db, userId, isActive) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      isActive,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error toggling user active status:', error);
    throw error;
  }
};

/**
 * Delete user
 */
export const deleteUser = async (db, userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await deleteDoc(userRef);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Check if user has specific role
 */
export const hasRole = (user, role) => {
  return user && user.role === role;
};

/**
 * Check if user is admin
 */
export const isAdmin = (user) => {
  return hasRole(user, USER_ROLES.ADMIN);
};

/**
 * Check if user is shared user
 */
export const isSharedUser = (user) => {
  return hasRole(user, USER_ROLES.SHARED);
};

/**
 * Check if user has public access
 */
export const isPublicAccess = (user) => {
  return !user || hasRole(user, USER_ROLES.PUBLIC);
};

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return false;
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

